import React, {useCallback, useEffect, useState} from 'react'
import {Form, FormDataContext} from '@modifi/plugin-forms'
import {Col, DangerAlert, Row} from '@modifi/ui'
import {formatNumber, useTranslation} from '@modifi/plugin-i18n'
import {TRADE_PARTNER_EXPECTED_FINANCING_LIMIT} from '@modifi/plugin-onboarding'
import {ProductType, ProductTypeVal} from '@modifi/enums'

import {ConnectedFormFooter} from '../../../../../plugins/onboarding/components/form-cta-footer'
import BuyerFormContent from './buyer-form-content'
import {mapFormDataToRequest} from '../../../../seller-trade-partners/components/company-data/utils'
import {EXPECTED_FINANCING_LIMIT_CAP} from '../../../../seller-trade-partners/constants'
import {EntityFormData} from '../../../../../types/common'
import {CompanyUnlockRefresher} from '../../../../../plugins/onboarding/components/company-unlock-refresher'
import {aFetchSellerTradePartners} from '../../../../../data/seller-trade-partners'

export type BuyerFormProps = {
  externalBuyerId?: string
  onSubmit: (changes: Record<string, unknown>, externalBuyerId?: string) => void
  onDiscard: () => void
  hasError: boolean
  saveErrorMessage?: string
  savePending: boolean
  formData: EntityFormData
  initializeDocs: (externalBuyerId: string) => void
  openLimitConfirmationDialog: (params: {
    limitAmount: string
    submitTradePartner: () => void
  }) => void
  fetchCurrencies: (productType: ProductTypeVal) => void
  buyerCount: number
  hideBuyerForm: () => void
  redirectToCompanyFinancials: () => void
}

const BuyerForm: React.FC<BuyerFormProps> = ({
  externalBuyerId,
  onSubmit,
  onDiscard,
  hasError,
  saveErrorMessage = '',
  savePending,
  formData,
  initializeDocs,
  openLimitConfirmationDialog,
  fetchCurrencies,
  buyerCount,
  hideBuyerForm,
  redirectToCompanyFinancials,
}) => {
  const {t} = useTranslation(['charon', 'plugin-forms'])
  const [discardChangeCounter, setDiscardChangeCounter] = useState(0)
  const [selectedCompany, setSelectedCompany] = useState(null as any)

  useEffect(() => {
    externalBuyerId && initializeDocs(externalBuyerId)
  }, [initializeDocs, externalBuyerId])

  const handleSubmit = useCallback(
    (changes: Record<string, any>) => {
      let dataOnSelection = changes

      if (selectedCompany) {
        dataOnSelection = {
          ...mapFormDataToRequest(changes, true),
          companyIds: selectedCompany?.companyIds || selectedCompany?.suggestion?.companyIds,
        }

        if (!externalBuyerId) {
          window.dataLayer = window.dataLayer || []
          window.dataLayer.push({
            event: 'pre_onboarding_buyer_creation_from_list',
            element_id: 'pre_onboarding_buyer_creation_from_list',
            element_type: 'button',
          })
        }
      } else if (!externalBuyerId) {
        // case when manual selection and new company creation

        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({
          event: 'pre_onboarding_manual_buyer_creation',
          element_id: 'pre_onboarding_manual_buyer_creation',
          element_type: 'button',
        })
      }

      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: 'pre_onboarding_buyer_creation',
        element_id: 'pre_onboarding_buyer_creation',
        element_type: 'button',
      })

      const expectedFinancingLimit = changes[TRADE_PARTNER_EXPECTED_FINANCING_LIMIT]

      if (expectedFinancingLimit > EXPECTED_FINANCING_LIMIT_CAP.medium) {
        openLimitConfirmationDialog({
          limitAmount: formatNumber(expectedFinancingLimit, {
            currency: 'USD',
            trimDecimals: true,
            currencyDisplay: 'symbol',
          }),
          submitTradePartner: () => {
            onSubmit && onSubmit(dataOnSelection, externalBuyerId)
          },
        })
      } else {
        onSubmit && onSubmit(dataOnSelection, externalBuyerId)
      }
    },
    [externalBuyerId, onSubmit, openLimitConfirmationDialog, selectedCompany]
  )

  const handelDiscard = useCallback(() => {
    setDiscardChangeCounter(prevCounter => prevCounter + 1)
    onDiscard && onDiscard()
  }, [onDiscard])

  useEffect(() => {
    fetchCurrencies(ProductType.SELLER_FINANCING)
  }, [fetchCurrencies])

  useEffect(() => {
    handelDiscard()
  }, [handelDiscard])

  const handleCompanySelect = useCallback(
    (companyDetails: any) => {
      if (saveErrorMessage) {
        onDiscard && onDiscard()
      }
      setSelectedCompany(companyDetails)
    },
    [saveErrorMessage, onDiscard]
  )

  const handleBackButtonClick = () => {
    if (buyerCount) {
      hideBuyerForm()
    } else {
      redirectToCompanyFinancials()
    }
  }

  return (
    <>
      {hasError && saveErrorMessage && (
        <Row>
          <Col>
            <DangerAlert>{saveErrorMessage}</DangerAlert>
          </Col>
        </Row>
      )}
      <Form
        isLoading={savePending}
        defaultValues={formData?.defaultValues}
        initialValues={formData?.initialValues}
        onSubmit={handleSubmit}
        onDiscard={handelDiscard}
      >
        <BuyerFormContent
          discardChangeCounter={discardChangeCounter}
          onCompanySelect={handleCompanySelect}
        />
        <CompanyUnlockRefresher
          refetch={(): any[] => [aFetchSellerTradePartners()]}
          initialValues={formData?.initialValues}
          defaultValues={formData?.defaultValues}
        />
        <FormDataContext.Consumer>
          {({hasChanges}) => (
            <ConnectedFormFooter
              hasError={hasError}
              hasNavLock={false}
              loading={savePending}
              hasCustomFormFooter
              hasBackButton
              canMoveBack
              onBackButtonClick={handleBackButtonClick}
              isAlwaysVisible
              submitLabel={t('sub-section-footer.cta-submit')}
              disableSubmit={!hasChanges || (!externalBuyerId && !selectedCompany)}
              isFinalStep
            />
          )}
        </FormDataContext.Consumer>
      </Form>
    </>
  )
}

export default BuyerForm
